/**
 * Auto-save utility for task/ticket fields
 * Handles localStorage operations for draft content
 */
import { AUTO_SAVE } from './appConstants';

const AUTO_SAVE_PREFIX = AUTO_SAVE.PREFIX;
const AUTO_SAVE_DEBOUNCE_MS = AUTO_SAVE.DEBOUNCE_MS;
const DRAFT_EXPIRY_MS = AUTO_SAVE.EXPIRY_MS;
const CREATE_TASK_FORM_DRAFT_FIELD = 'createTaskForm';
const CREATE_TASK_DRAFT_ID_PREFIX = 'new_project_';

/**
 * Clean up oldest drafts to free storage space
 */
const cleanupOldestDrafts = () => {
  try {
    const drafts = [];
    
    // Collect all draft keys with timestamps
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(AUTO_SAVE_PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          drafts.push({ 
            key, 
            timestamp: data?.timestamp || 0 
          });
        } catch (e) {
          // Invalid data, mark for removal
          drafts.push({ key, timestamp: 0 });
        }
      }
    }
    
    // Sort by timestamp (oldest first)
    drafts.sort((a, b) => a.timestamp - b.timestamp);
    
    // Remove oldest 50% of drafts
    const toRemove = Math.ceil(drafts.length / 2);
    for (let i = 0; i < toRemove; i++) {
      localStorage.removeItem(drafts[i].key);
    }
  } catch (error) {
    console.error('Failed to cleanup old drafts:', error);
  }
};

const hasRichTextContent = (content = '') => {
  if (typeof content !== 'string' || !content) return false;

  const textContent = content
    .replace(/<br\s*\/?>/gi, ' ')      // Replace <br> with space
    .replace(/&nbsp;/gi, ' ')           // Replace &nbsp; with space
    .replace(/<[^>]*>/g, '')            // Remove all HTML tags
    .replace(/\s+/g, ' ')               // Collapse multiple spaces/newlines
    .trim();                             // Trim whitespace

  return Boolean(textContent);
};

const normalizeCreateTaskDraftValue = (fieldName, value) => {
  if (fieldName === 'description' || fieldName === 'acceptanceCriteria') {
    return hasRichTextContent(value) ? value : '';
  }

  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return value;
  return value ?? '';
};

const areDraftValuesEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const hasChecklistContent = (value) => {
  if (!value) return false;

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (Array.isArray(value.checklists)) {
    return value.checklists.length > 0;
  }

  return false;
};

/**
 * Clean up expired drafts (older than 24 hours)
 */
export const cleanupExpiredDrafts = () => {
  try {
    const now = Date.now();
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(AUTO_SAVE_PREFIX)) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data && data.timestamp) {
            const age = now - data.timestamp;
            if (age > DRAFT_EXPIRY_MS) {
              keysToRemove.push(key);
            }
          }
        } catch (e) {
          // Invalid data, remove it
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Failed to cleanup expired drafts:', error);
  }
};

/**
 * Generate storage key for a specific field
 * @param {string} ticketId - Ticket/task ID (use 'new' for new tickets)
 * @param {string} fieldName - Field name (description, acceptanceCriteria, comment)
 * @returns {string} Storage key
 */
export const getAutoSaveKey = (ticketId, fieldName) => {
  return `${AUTO_SAVE_PREFIX}${ticketId}_${fieldName}`;
};

/**
 * Check whether a field belongs to comment autosave flow
 * @param {string} fieldName - Field name
 * @returns {boolean} True if the field is a new/edit comment draft field
 */
export const isCommentDraftField = (fieldName = '') => {
  return fieldName === 'comment' || fieldName.startsWith('comment_');
};

/**
 * Get the autosave field name for editing a specific comment
 * @param {string} commentId - Comment ID
 * @returns {string} Field name for the comment edit draft
 */
export const getCommentEditDraftFieldName = (commentId) => {
  return `comment_edit_${commentId}`;
};

/**
 * Get the autosave field name for replying to a comment/reply
 * @param {string} commentId - Top-level comment ID
 * @param {string|null} parentReplyId - Parent reply ID for nested replies
 * @returns {string} Field name for the reply draft
 */
export const getCommentReplyDraftFieldName = (commentId, parentReplyId = null) => {
  return `comment_reply_${commentId}__${parentReplyId || 'root'}`;
};

/**
 * Get the autosave field name for create-task form data
 * @returns {string} Field name for the create-task form draft
 */
export const getCreateTaskFormDraftFieldName = () => CREATE_TASK_FORM_DRAFT_FIELD;

/**
 * Get the autosave ticket ID for create-task drafts scoped to a project
 * @param {string|number|null|undefined} projectId - Project ID for the draft
 * @returns {string} Ticket ID used by create-task autosave
 */
export const getCreateTaskDraftId = (projectId) => {
  if (projectId === null || projectId === undefined || projectId === '') {
    return 'new';
  }

  return `${CREATE_TASK_DRAFT_ID_PREFIX}${projectId}`;
};

/**
 * Extract the edited comment ID from an autosave field name
 * @param {string} fieldName - Autosave field name
 * @returns {string|null} Comment ID if this is an edit draft field
 */
export const getCommentEditIdFromFieldName = (fieldName = '') => {
  if (!fieldName.startsWith('comment_edit_')) return null;
  return fieldName.slice('comment_edit_'.length) || null;
};

/**
 * Extract reply target metadata from an autosave field name
 * @param {string} fieldName - Autosave field name
 * @returns {{ commentId: string, parentReplyId: string|null }|null} Reply target metadata
 */
export const getCommentReplyTargetFromFieldName = (fieldName = '') => {
  const match = fieldName.match(/^comment_reply_(.+?)__(.+)$/);
  if (!match) return null;

  return {
    commentId: match[1],
    parentReplyId: match[2] === 'root' ? null : match[2]
  };
};

/**
 * Get normalized comment draft metadata from a field name
 * @param {string} fieldName - Autosave field name
 * @returns {{ mode: 'new'|'edit'|'reply', commentId: string|null, parentReplyId: string|null }|null}
 */
export const getCommentDraftMetaFromFieldName = (fieldName = '') => {
  if (fieldName === 'comment') {
    return { mode: 'new', commentId: null, parentReplyId: null };
  }

  const editId = getCommentEditIdFromFieldName(fieldName);
  if (editId) {
    return { mode: 'edit', commentId: editId, parentReplyId: null };
  }

  const replyTarget = getCommentReplyTargetFromFieldName(fieldName);
  if (replyTarget) {
    return {
      mode: 'reply',
      commentId: replyTarget.commentId,
      parentReplyId: replyTarget.parentReplyId
    };
  }

  return null;
};

/**
 * Save draft content to localStorage
 * @param {string} ticketId - Ticket/task ID
 * @param {string} fieldName - Field name
 * @param {string} content - Content to save
 */
export const saveDraft = (ticketId, fieldName, content) => {
  try {
    const key = getAutoSaveKey(ticketId, fieldName);
    
    // Strip HTML tags and decode HTML entities to check for actual text content
    const textContent = content 
      ? content
          .replace(/<br\s*\/?>/gi, ' ')           // Replace <br> with space
          .replace(/&nbsp;/gi, ' ')                // Replace &nbsp; with space
          .replace(/<[^>]*>/g, '')                 // Remove all HTML tags
          .replace(/\s+/g, ' ')                    // Collapse multiple spaces
          .trim()                                   // Trim whitespace
      : '';
    
    if (!textContent) {
      localStorage.removeItem(key);
      return;
    }
    
    // Try to save
    try {
      localStorage.setItem(key, JSON.stringify({
        content,
        timestamp: Date.now()
      }));
    } catch (error) {
      // Storage full - cleanup oldest drafts and retry
      if (error.name === 'QuotaExceededError' || error.code === 22) {
        cleanupOldestDrafts();
        try {
          localStorage.setItem(key, JSON.stringify({
            content,
            timestamp: Date.now()
          }));
        } catch (retryError) {
          // Silent fail if still no space
        }
      }
    }
  } catch (error) {
    console.error('Failed to save draft:', error);
  }
};

/**
 * Load draft content from localStorage
 * @param {string} ticketId - Ticket/task ID
 * @param {string} fieldName - Field name
 * @returns {string|null} Saved content or null
 */
export const loadDraft = (ticketId, fieldName) => {
  try {
    // Clean up expired drafts on every load
    cleanupExpiredDrafts();
    
    const key = getAutoSaveKey(ticketId, fieldName);
    const saved = localStorage.getItem(key);
    if (saved) {
      const { content, timestamp } = JSON.parse(saved);
      
      // Check if draft is expired (older than 24 hours)
      const age = Date.now() - timestamp;
      if (age > DRAFT_EXPIRY_MS) {
        localStorage.removeItem(key);
        return null;
      }
      
      return content;
    }
  } catch (error) {
    console.error('Failed to load draft:', error);
  }
  return null;
};

/**
 * Clear draft content from localStorage
 * @param {string} ticketId - Ticket/task ID
 * @param {string} fieldName - Field name
 */
export const clearDraft = (ticketId, fieldName) => {
  try {
    const key = getAutoSaveKey(ticketId, fieldName);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to clear draft:', error);
  }
};

/**
 * Save structured draft content to localStorage
 * @param {string} ticketId - Ticket/task ID
 * @param {string} fieldName - Field name
 * @param {any} content - Structured content to save
 */
export const saveStructuredDraft = (ticketId, fieldName, content) => {
  try {
    const key = getAutoSaveKey(ticketId, fieldName);
    const hasContent = content && (
      (Array.isArray(content) && content.length > 0) ||
      (typeof content === 'object' && !Array.isArray(content) && Object.keys(content).length > 0) ||
      typeof content !== 'object'
    );

    if (hasContent) {
      localStorage.setItem(key, JSON.stringify({
        content,
        timestamp: Date.now()
      }));
    } else {
      localStorage.removeItem(key);
    }
  } catch (error) {
    console.error('Failed to save structured draft:', error);
  }
};

/**
 * Load structured draft content from localStorage
 * @param {string} ticketId - Ticket/task ID
 * @param {string} fieldName - Field name
 * @returns {any|null} Saved structured content or null
 */
export const loadStructuredDraft = (ticketId, fieldName) => {
  try {
    cleanupExpiredDrafts();

    const key = getAutoSaveKey(ticketId, fieldName);
    const saved = localStorage.getItem(key);
    if (saved) {
      const { content, timestamp } = JSON.parse(saved);
      const age = Date.now() - timestamp;
      if (age > DRAFT_EXPIRY_MS) {
        localStorage.removeItem(key);
        return null;
      }
      return content;
    }
  } catch (error) {
    console.error('Failed to load structured draft:', error);
  }
  return null;
};

/**
 * Clear selected drafts for a specific ticket
 * @param {string} ticketId - Ticket/task ID
 * @param {string[]} fields - Field names to clear
 */
export const clearDraftFields = (ticketId, fields = []) => {
  try {
    fields.forEach(field => clearDraft(ticketId, field));
  } catch (error) {
    console.error('Failed to clear selected drafts:', error);
  }
};

/**
 * Clear all comment-related drafts for a ticket except the currently active one
 * @param {string} ticketId - Ticket/task ID
 * @param {string|null} activeFieldName - Field name to preserve, if any
 */
export const clearOtherCommentDrafts = (ticketId, activeFieldName = null) => {
  try {
    if (!ticketId) return;

    const activeKey = activeFieldName ? getAutoSaveKey(ticketId, activeFieldName) : null;
    const commentKey = getAutoSaveKey(ticketId, 'comment');
    const commentPrefix = getAutoSaveKey(ticketId, 'comment_');

    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key) continue;

      const matchesCommentDraft = key === commentKey || key.startsWith(commentPrefix);
      if (matchesCommentDraft && key !== activeKey) {
        localStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error('Failed to clear other comment drafts:', error);
  }
};

/**
 * Get the latest active comment draft metadata for a ticket
 * @param {string} ticketId - Ticket/task ID
 * @returns {{ fieldName: string, mode: 'new'|'edit', commentId: (string|null), timestamp: number } | null}
 */
export const getActiveCommentDraft = (ticketId) => {
  try {
    if (!ticketId) return null;

    cleanupExpiredDrafts();

    const drafts = [];
    const commentKey = getAutoSaveKey(ticketId, 'comment');
    const commentPrefix = getAutoSaveKey(ticketId, 'comment_');

    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || (key !== commentKey && !key.startsWith(commentPrefix))) continue;

      const rawValue = localStorage.getItem(key);
      if (!rawValue) continue;

      try {
        const parsed = JSON.parse(rawValue);
        const fieldName = key.slice(`${AUTO_SAVE_PREFIX}${ticketId}_`.length);
        const meta = getCommentDraftMetaFromFieldName(fieldName);
        if (!meta) continue;
        drafts.push({
          fieldName,
          mode: meta.mode,
          commentId: meta.commentId,
          parentReplyId: meta.parentReplyId,
          timestamp: parsed?.timestamp || 0
        });
      } catch (error) {
        // Ignore malformed entries here; cleanup handles them elsewhere.
      }
    }

    if (drafts.length === 0) return null;

    drafts.sort((a, b) => b.timestamp - a.timestamp);
    return drafts[0];
  } catch (error) {
    return null;
  }
};

/**
 * Finds the path of ancestor comment IDs to a target comment/reply
 * @param {Array} commentsList - Array of comments to search
 * @param {string|number} id - Comment ID to find
 * @returns {Array} - Ordered path of IDs from top-level comment to target
 */
const matchesCommentId = (commentId, targetId) => String(commentId) === String(targetId);

export const findCommentPathById = (commentsList, id) => {
  if (!id || !Array.isArray(commentsList)) return [];

  for (const comment of commentsList) {
    if (matchesCommentId(comment.id, id)) return [comment.id];

    if (comment.replies?.length) {
      const nestedPath = findCommentPathById(comment.replies, id);
      if (nestedPath.length > 0) {
        return [comment.id, ...nestedPath];
      }
    }
  }

  return [];
};

/**
 * Build the autosave payload for create-task form data
 * Only values that differ from the default create-task state are persisted.
 * Attachments are intentionally excluded.
 * @param {Object} formData - Current create-task form data
 * @param {Object} defaultFormData - Default create-task form data
 * @returns {Object} Reduced draft payload
 */
export const buildCreateTaskFormDraft = (formData = {}, defaultFormData = {}) => {
  const trackedFields = [
    'title',
    'taskType',
    'priority',
    'status',
    'assignees',
    'project',
    'dueDate',
    'dueTime',
    'startDate',
    'description',
    'labels',
    'sprint',
    'acceptanceCriteria',
    'checklist',
    'estimateTime',
    'is_recurring',
    'recurrence_config'
  ];

  return trackedFields.reduce((draft, fieldName) => {
    const currentValue = normalizeCreateTaskDraftValue(fieldName, formData[fieldName]);
    const defaultValue = normalizeCreateTaskDraftValue(fieldName, defaultFormData[fieldName]);

    if (fieldName === 'checklist') {
      if (hasChecklistContent(currentValue) || !areDraftValuesEqual(currentValue, defaultValue)) {
        if (!areDraftValuesEqual(currentValue, defaultValue)) {
          draft[fieldName] = currentValue;
        }
      }
      return draft;
    }

    if (!areDraftValuesEqual(currentValue, defaultValue)) {
      draft[fieldName] = currentValue;
    }

    return draft;
  }, {});
};

/**
 * Apply a saved create-task draft on top of default create-task form data
 * @param {Object} defaultFormData - Default create-task form data
 * @param {Object|null} draftData - Saved create-task draft data
 * @returns {Object} Restored create-task form data
 */
export const applyCreateTaskFormDraft = (defaultFormData = {}, draftData = null) => {
  if (!draftData || typeof draftData !== 'object') {
    return { ...defaultFormData };
  }

  return {
    ...defaultFormData,
    ...draftData
  };
};

/**
 * Check whether a create-task draft has checklist content
 * @param {any} checklistValue - Checklist value from the create-task form
 * @returns {boolean} True if checklist content exists
 */
export const hasCreateTaskChecklistContent = (checklistValue) => hasChecklistContent(checklistValue);

/**
 * Clear all drafts for a specific ticket
 * @param {string} ticketId - Ticket/task ID
 */
export const clearAllDrafts = (ticketId) => {
  try {
    clearDraftFields(ticketId, ['description', 'acceptanceCriteria', 'comment']);
  } catch (error) {
    console.error('Failed to clear all drafts:', error);
  }
};

/**
 * Create debounced auto-save function
 * @param {Function} saveFunction - Function to call for saving
 * @param {number} delay - Debounce delay in ms
 * @returns {Function} Debounced function
 */
export const createDebouncedAutoSave = (saveFunction, delay = AUTO_SAVE_DEBOUNCE_MS) => {
  let timeoutId = null;
  
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      saveFunction(...args);
      timeoutId = null;
    }, delay);
  };
};

/**
 * Check if draft exists for a ticket field
 * @param {string} ticketId - Ticket/task ID
 * @param {string} fieldName - Field name
 * @returns {boolean} True if draft exists
 */
export const hasDraft = (ticketId, fieldName) => {
  try {
    const key = getAutoSaveKey(ticketId, fieldName);
    return localStorage.getItem(key) !== null;
  } catch (error) {
    return false;
  }
};
